import {Checkbox, FormControlLabel} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import {editLockedViolatorAddress, getHolderLookupTrail} from "../../actions/violations";
import CountrySelector, {convertLanguageCode} from "../../common/address/CountrySelector";
import Button from "../../common/button/button";
import LabelWithHR from "../../common/labels/labelWithHR";
import {newModalStyle, submitButtonStyle} from "../../reusable/styles";
import "react-datepicker/dist/react-datepicker.css";
import Style from "./modalEditTicket.module.css";
import HolderLookupTrail from "./holderLookupTrail";

const ViolatorsAddressLockedModal = (props) => {
  const { t, i18n } = useTranslation();
  const { handleSubmit, control, register, formState: { errors } } = useForm({
    mode: 'onSubmit'
  });
  const dispatch = useDispatch();
  const appLanguageCode = localStorage.getItem("beppoSelectedLanguage") || "deu";
  const apiLanguageCode = convertLanguageCode(appLanguageCode, 'three-letter');
  const [countryFieldHighlighted, setCountryFieldHighlighted] = useState(false);
  const [violatorAddress, setViolatorAddress] = useState({
    ticketId: props?.ticket?.id,
    name: props?.ticket?.violatorAddress?.name,
    street: props?.ticket?.violatorAddress?.street,
    postalCode: props?.ticket?.violatorAddress?.postalCode,
    area: props?.ticket?.violatorAddress?.area,
    country: props?.ticket?.violatorAddress?.country,
    additionalInformation : props?.ticket?.violatorAddress?.additionalInformation,
    doNotReuse: props?.ticket?.violatorAddress?.doNotReuse,
  });

  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingStreets, setIsLoadingStreets] = useState(false);

  // BEP-1821: holder lookup trail (local state — not redux, to avoid stale cross-ticket data)
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);
  const countrySelectRef = useRef(null);

  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const mapRef = useRef(null);
  const googleMapsScriptLoaded = useRef(false);
  const googleMapsInitialized = useRef(false);

  useEffect(() => {
    const initGoogleMaps = () => {
      if (googleMapsInitialized.current) return;
      if (window.google && window.google.maps && !googleMapsInitialized.current) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        const mapDiv = document.createElement('div');
        mapDiv.style.display = 'none';
        document.body.appendChild(mapDiv);
        mapRef.current = new window.google.maps.Map(mapDiv);
        placesServiceRef.current = new window.google.maps.places.PlacesService(mapRef.current);
        googleMapsInitialized.current = true;
      }
    };

    if (window.google && window.google.maps && window.google.maps.places) {
      googleMapsScriptLoaded.current = true;
      initGoogleMaps();
    } else if (!googleMapsScriptLoaded.current) {
      googleMapsScriptLoaded.current = true;
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existingScript) {
        existingScript.addEventListener('load', initGoogleMaps);
      } else {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initGoogleMaps;
        document.head.appendChild(script);
      }
    }
    return () => {
      if (mapRef.current && mapRef.current.getDiv()) {
        const mapDiv = mapRef.current.getDiv();
        if (mapDiv && mapDiv.parentNode) {
          mapDiv.parentNode.removeChild(mapDiv);
        }
      }
    };
  }, []);

  // BEP-1821: fetch holder lookup trail on open
  useEffect(() => {
    const referenceNumber = props?.ticket?.number;
    if (!referenceNumber) return;
    setTrailLoading(true);
    getHolderLookupTrail(referenceNumber)
      .then((data) => setTrail(data))
      .finally(() => setTrailLoading(false));
  }, [props?.ticket?.number]);

  const isStatusSelected = () => {
    return violatorAddress.status && violatorAddress.status !== "-";
  };

  const checkCountrySelected = () => {
    if (isStatusSelected()) {
      setCountryFieldHighlighted(false);
      return true;
    }

    if (!violatorAddress.country) {
      setCountryFieldHighlighted(true);
      if (countrySelectRef.current) {
        countrySelectRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        countrySelectRef.current.focus();
      }
      return false;
    }
    setCountryFieldHighlighted(false);
    return true;
  };

  const fetchStreetSuggestions = (input) => {
    if (!autocompleteServiceRef.current || input.length < 2 || !violatorAddress.country) {
      setStreetSuggestions([]);
      return;
    }
    setIsLoadingStreets(true);
    autocompleteServiceRef.current.getPlacePredictions({
      input,
      types: ['address'],
      componentRestrictions: {
        country: violatorAddress.country.toLowerCase()
      },
      sessionToken: sessionTokenRef.current
    }, (predictions, status) => {
      setIsLoadingStreets(false);
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setStreetSuggestions(predictions);
        setShowSuggestions(true);
      } else {
        setStreetSuggestions([]);
      }
    });
  };

  const handleSelectStreet = (placeId) => {
    if (!placesServiceRef.current) return;
    placesServiceRef.current.getDetails({
      placeId,
      fields: ['address_components', 'formatted_address'],
      sessionToken: sessionTokenRef.current
    }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        let streetNumber = '';
        let streetName = '';
        let newPostalCode = '';
        let newArea = '';
        const isGermany = violatorAddress.country.toLowerCase() === 'de';
        const allowedAreaTypes = isGermany
            ? ['locality']
            : ['locality', 'administrative_area_level_3'];
        place.address_components.forEach(component => {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (types.includes('route')) {
            streetName = component.long_name;
          }
          if (types.includes('postal_code')) {
            newPostalCode = component.long_name;
          }
          if (allowedAreaTypes.some(t => types.includes(t))) {
            if(newArea === '') {
                newArea = component.long_name;
            }
          }

        });
        const fullStreet = violatorAddress.country === 'FR' ||
                           violatorAddress.country === 'ES' ||
                           violatorAddress.country === 'IT'
                           ? streetNumber ? `${streetNumber} ${streetName}` : streetName
                           : streetNumber ? `${streetName} ${streetNumber}` : streetName;
        const updatedAddress = {
          ...violatorAddress,
          street: fullStreet
        };
        if (newPostalCode) {
          updatedAddress.postalCode = newPostalCode;
        }
        if (newArea) {
          updatedAddress.area = newArea;
        }
        setViolatorAddress(updatedAddress);
        setStreetSuggestions([]);
        setShowSuggestions(false);
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    });
  };

  const handleStreetChange = (e) => {
    if (!isStatusSelected() && !checkCountrySelected()) {
      return;
    }
    const value = e.target.value;
    setViolatorAddress({
      ...violatorAddress,
      street: value,
    });
    if (value && violatorAddress.country) {
      fetchStreetSuggestions(value);
    } else {
      setStreetSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleStreetFocus = () => {
    if (!isStatusSelected() && !checkCountrySelected()) {
      return;
    }
    if (violatorAddress.street && violatorAddress.country) {
      fetchStreetSuggestions(violatorAddress.street);
    }
  };

  const handlePostalCodeChange = (e) => {
    if (!isStatusSelected() && !checkCountrySelected()) {
      return;
    }
    setViolatorAddress({
      ...violatorAddress,
      postalCode: e.target.value,
    });
  };

  const handleAreaChange = (e) => {
    if (!isStatusSelected() && !checkCountrySelected()) {
      return;
    }
    setViolatorAddress({
      ...violatorAddress,
      area: e.target.value,
    });
  };

  const handleCountryChange = (countryData) => {
    setViolatorAddress({
      ...violatorAddress,
      country: countryData.code,
      countryName: countryData.name
    });
    setStreetSuggestions([]);
    setShowSuggestions(false);
    setCountryFieldHighlighted(false);
  };

  const handleStatusChange = (e) => {
    setViolatorAddress({
      ...violatorAddress,
      status: e.target.value
    });
  };

  const validateForm = () => {
    if (isStatusSelected()) {
      return true;
    }

    return (
      violatorAddress.name &&
      violatorAddress.street &&
      violatorAddress.postalCode &&
      violatorAddress.area &&
      violatorAddress.country
    );
  };

  const [submitting, setSubmitting] = useState(false);

  const editViolatorAddress = async () => {
    if (!validateForm()) {
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(editLockedViolatorAddress(violatorAddress));
    } catch (e) {
      // the action already showed the error notification; stay open so the address is not lost
      setSubmitting(false);
      return;
    }
    props.close();
  };

  const hasTrail = (trail?.episodes?.length ?? 0) > 0;

  return (
    <Modal
      open={props.open}
      closeOnEsc={!submitting}
      closeOnOverlayClick={!submitting}
      showCloseIcon={!submitting}
      onClose={props.close}
      center
      styles={newModalStyle({ width: hasTrail ? "920px" : "450px" })}
    >
      <div style={hasTrail ? { display: "flex", gap: "24px", alignItems: "flex-start" } : {}}>
      <div style={hasTrail ? { width: "420px", flexShrink: 0 } : {}}>
      <form onSubmit={handleSubmit(editViolatorAddress)}>
        <div className="modal__title">
          {props?.ticket?.licensePlate}{" "}
          {props.ticket.violatorAddress?.locked && (
            <>- {t("ViolatorsAddressModal.LPLocked")}</>
          )}
        </div>
        <div>
          <LabelWithHR label={t("ViolatorsAddressModal.name")} />
          <Controller
            name="name"
            control={control}
            render={() => (
              <input
                className={Style.textStyleInputEditModal}
                name="name"
                required={!isStatusSelected()}
                onChange={(e) => {
                  setViolatorAddress({
                    ...violatorAddress,
                    name: e.target.value,
                  });
                }}
                defaultValue={violatorAddress.name}
              />
            )}
          />
        </div>

        <div>
          <LabelWithHR label={t("ViolatorsAddressModal.additionalInformation")} />
          <Controller
            name="additionalInformation"
            control={control}
            render={() => (
              <input
                className={Style.textStyleInputEditModal}
                name="additionalInformation"
                onChange={(e) => {
                  setViolatorAddress({
                    ...violatorAddress,
                    additionalInformation: e.target.value,
                  });
                }}
                defaultValue={violatorAddress.additionalInformation}
              />
            )}
          />
        </div>
        <div>
          <LabelWithHR label={t("ViolatorsAddressModal.country")} />
          <Controller
            name="country"
            control={control}
            render={() => (
              <CountrySelector
                selectedCountry={violatorAddress.country}
                onCountryChange={handleCountryChange}
                t={t}
                apiLanguageCode={apiLanguageCode}
                className={Style.modalInput}
              />
            )}
          />
          {countryFieldHighlighted && !isStatusSelected() && (
            <div className={Style.errorMessage}>
              {t("ViolatorsAddressModal.pleaseSelectCountry")}
            </div>
          )}
        </div>
        <div className={Style.streetSuggestionsContainer}>
          <LabelWithHR label={t("ViolatorsAddressModal.street")} />
          <Controller
            name="street"
            control={control}
            render={() => (
              <input
                className={Style.textStyleInputEditModal}
                onChange={handleStreetChange}
                onFocus={handleStreetFocus}
                required={!isStatusSelected()}
                onBlur={() => {
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                value={violatorAddress.street || ''}
                autoComplete="off"
              />
            )}
          />
          {showSuggestions && streetSuggestions.length > 0 && (
            <ul
              className={Style.streetSuggestionsList}
            >
              {streetSuggestions.map(suggestion => (
                <li
                  key={suggestion.place_id}
                  className={Style.streetSuggestionItem}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectStreet(suggestion.place_id);
                  }}
                >
                  {suggestion.description}
                </li>
              ))}
            </ul>
          )}
          {isLoadingStreets && (
            <div className={Style.loadingIndicator}>
              {t("ViolatorsAddressModal.loading")}
            </div>
          )}
        </div>
        <div>
          <LabelWithHR label={t("ViolatorsAddressModal.postalCode")} />
          <Controller
            name="postalCode"
            control={control}
            render={() => (
              <input
                className={Style.textStyleInputEditModal}
                onChange={handlePostalCodeChange}
                required={!isStatusSelected()}
                onFocus={() => !isStatusSelected() && checkCountrySelected()}
                value={violatorAddress.postalCode}
              />
            )}
          />
        </div>
        <div>
          <LabelWithHR label={t("ViolatorsAddressModal.area")} />
          <Controller
            name="area"
            control={control}
            render={() => (
              <input
                className={Style.textStyleInputEditModal}
                onChange={handleAreaChange}
                required={!isStatusSelected()}
                onFocus={() => !isStatusSelected() && checkCountrySelected()}
                value={violatorAddress.area}
              />
            )}
          />
        </div>
        <div>
          <LabelWithHR label={t("ViolatorsAddressModal.status")} />
          <Controller
            name="status"
            control={control}
            render={() => (
              <select
                type="text"
                name={`status`}
                style={{ borderRadius: "5px", width:"100%", height:"3em", padding:"10px 5px" }}
                onChange={handleStatusChange}
              >
                <option disabled selected value>
                  {"-"}
                </option>
                <option value="REQUESTED" key={1}>
                  {t(`ViolatorsAddressModal.Statuses.requested`)}
                </option>
                <option value="UNKNOWN" key={2}>
                  {t(`ViolatorsAddressModal.Statuses.unknown`)}
                </option>
              </select>
            )}
          />
        </div>
        <FormControlLabel
          control={<Checkbox
            name="doNotReuse"
            checked={violatorAddress.doNotReuse}
            onChange={() => {
              setViolatorAddress({
                ...violatorAddress,
                doNotReuse: !violatorAddress.doNotReuse
              })
            }}
          />}
          label={t("ViolatorsAddressModal.doNotReuse")}
          sx={{
            marginTop: "3px",
            "& .MuiFormControlLabel-label": {
              fontFamily: "Gilroy-Regular",
              marginTop: "3px"
            }
          }}
        />
        <Button text={t("Buttons.save")} buttonStyle={submitButtonStyle} disabled={submitting} />
      </form>
      </div>
      {hasTrail && (
        <div style={{ flex: 1, minWidth: 0, borderLeft: "1px solid #e0e0e0", paddingLeft: "24px", maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <HolderLookupTrail episodes={trail.episodes} loading={trailLoading} />
        </div>
      )}
      </div>
    </Modal>
  );
};
export default ViolatorsAddressLockedModal;